import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '../hooks/useFetch';
import PageLoader from '../components/PageLoader';
import {
    ArrowRight,
    Mail,
    Bell,
    Sparkles,
    Clock,
    Calendar,
    Tag,
    User,
    Share2,
    Bookmark,
    Heart,
    TrendingUp,
    Search,
    X,
} from 'lucide-react';

function Blog() {
    const { data: postsData, loading, error } = useFetch('/blogs');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [visiblePosts, setVisiblePosts] = useState(6);

    // Extract unique categories from posts
    const categories = useMemo(() => {
        if (!postsData) return ['All'];
        const cats = postsData.map((p) => p.category).filter(Boolean);
        return ['All', ...new Set(cats)];
    }, [postsData]);

    // Filter posts based on search and category
    const filteredPosts = useMemo(() => {
        if (!postsData) return [];
        return postsData.filter((post) => {
            const matchesSearch =
                post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                post.tag?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [postsData, searchTerm, selectedCategory]);

    // Featured post (first with is_featured === true, or first post)
    const featuredPost = useMemo(() => {
        if (!postsData) return null;
        return postsData.find((p) => p.is_featured) || postsData[0] || null;
    }, [postsData]);

    // Loading state
    if (loading) {
        return <PageLoader message="Loading articles..." />;
    }

    // Error state
    if (error) {
        return (
            <div
                className="error-state"
                style={{
                    minHeight: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                }}
            >
                <p style={{ color: 'var(--danger)', fontSize: '18px' }}>Failed to load blog posts.</p>
                <p style={{ color: 'var(--muted)' }}>{error}</p>
            </div>
        );
    }

    return (
        <>
            {/* Hero Section - Premium */}
            <div className="page-hero" style={{ padding: '140px 32px 80px' }}>
                <div className="hero-premium-gradient-1"></div>
                <div className="hero-premium-gradient-2"></div>
                <div className="hero-premium-gradient-3"></div>

                <div style={{ position: 'relative', zIndex: 2, maxWidth: '900px', margin: '0 auto' }}>
                    <div className="eyebrow light" style={{ display: 'inline-flex', marginBottom: '20px' }}>
                        <Sparkles size={14} style={{ marginRight: '6px' }} />
                        Blog & Insights
                    </div>

                    <h1
                        style={{
                            fontSize: 'clamp(2.5rem, 5vw, 4.2rem)',
                            fontWeight: 700,
                            lineHeight: 1.05,
                            letterSpacing: '-0.03em',
                            marginBottom: '16px',
                        }}
                    >
                        Insights for <span className="highlight">church leaders</span>
                    </h1>

                    <p
                        style={{
                            fontSize: 'clamp(1rem, 1.2vw, 1.2rem)',
                            color: 'rgba(255,255,255,0.6)',
                            maxWidth: '560px',
                            margin: '0 auto 32px',
                            lineHeight: 1.8,
                        }}
                    >
                        Practical advice on member care, church growth, and using technology to keep your
                        congregation connected.
                    </p>

                    {/* Search Bar */}
                    <div
                        style={{
                            maxWidth: '480px',
                            margin: '0 auto',
                            background: 'rgba(255,255,255,0.06)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <Search
                            size={20}
                            style={{
                                color: 'rgba(255,255,255,0.3)',
                                marginLeft: '12px',
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search articles..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                flex: 1,
                                padding: '12px 8px',
                                background: 'transparent',
                                border: 'none',
                                color: '#fff',
                                fontSize: '14px',
                                outline: 'none',
                                fontFamily: 'inherit',
                            }}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'rgba(255,255,255,0.3)',
                                    cursor: 'pointer',
                                    padding: '8px 12px',
                                }}
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Featured Post Section */}
            {!searchTerm && selectedCategory === 'All' && featuredPost && (
                <section style={{ padding: '60px 0 40px', background: 'var(--cream)' }}>
                    <div className="wrap">
                        <div
                            className="blog-featured"
                            style={{
                                background: '#fff',
                                padding: '48px',
                                borderRadius: 'var(--radius-xl)',
                                boxShadow: 'var(--shadow-md)',
                            }}
                        >
                            <div
                                className="blog-featured-visual"
                                style={{
                                    backgroundImage: `url(${featuredPost.featured_image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    minHeight: '300px',
                                    borderRadius: 'var(--radius-lg)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background:
                                            'linear-gradient(135deg, rgba(10,22,40,0.3) 0%, rgba(26,46,84,0.1) 100%)',
                                    }}
                                >
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '20px',
                                            left: '20px',
                                            background: 'var(--gradient-gold)',
                                            padding: '6px 16px',
                                            borderRadius: 'var(--radius-full)',
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            color: 'var(--navy)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                        }}
                                    >
                                        <Sparkles size={12} />
                                        Featured
                                    </div>
                                </div>
                            </div>

                            <div style={{ padding: '32px 0 0' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '16px',
                                        flexWrap: 'wrap',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <span
                                        style={{
                                            background: 'var(--cream)',
                                            padding: '4px 14px',
                                            borderRadius: 'var(--radius-full)',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: 'var(--ink-soft)',
                                        }}
                                    >
                                        {featuredPost.tag}
                                    </span>
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontSize: '12px',
                                            color: 'var(--muted)',
                                        }}
                                    >
                                        <Calendar size={14} />
                                        {featuredPost.published_at}
                                    </span>
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontSize: '12px',
                                            color: 'var(--muted)',
                                        }}
                                    >
                                        <Clock size={14} />
                                        {featuredPost.read_time} min read
                                    </span>
                                </div>

                                <h2
                                    style={{
                                        fontSize: 'clamp(1.5rem, 2.5vw, 2.2rem)',
                                        color: 'var(--navy)',
                                        marginBottom: '12px',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {featuredPost.title}
                                </h2>

                                <p
                                    style={{
                                        fontSize: '16px',
                                        color: 'var(--ink-soft)',
                                        lineHeight: 1.8,
                                        marginBottom: '20px',
                                        maxWidth: '600px',
                                    }}
                                >
                                    {featuredPost.excerpt}
                                </p>

                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px',
                                        flexWrap: 'wrap',
                                        marginBottom: '20px',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                background: 'var(--gradient-gold)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'var(--navy)',
                                                fontWeight: 700,
                                                fontSize: '14px',
                                            }}
                                        >
                                            {featuredPost.author?.split(' ').map((name) => name[0]).join('')}
                                        </div>
                                        <div>
                                            <div
                                                style={{
                                                    fontWeight: 600,
                                                    color: 'var(--navy)',
                                                    fontSize: '14px',
                                                }}
                                            >
                                                {featuredPost.author}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: '12px',
                                                    color: 'var(--muted)',
                                                }}
                                            >
                                                {featuredPost.author_role}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Link to={`/blog/${featuredPost.id}`} className="btn-primary">
                                    Read full article <ArrowRight size={18} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Blog Posts Section */}
            <section
                style={{
                    background: '#fff',
                    padding: '60px 0 80px',
                }}
            >
                <div className="wrap">
                    {/* Category Filter */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            flexWrap: 'wrap',
                            marginBottom: '40px',
                            paddingBottom: '24px',
                            borderBottom: '1px solid var(--border)',
                        }}
                    >
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                style={{
                                    padding: '8px 20px',
                                    borderRadius: 'var(--radius-full)',
                                    border:
                                        selectedCategory === category
                                            ? '2px solid var(--gold)'
                                            : '1px solid var(--border)',
                                    background: selectedCategory === category ? 'var(--gold)' : 'transparent',
                                    color: selectedCategory === category ? 'var(--navy)' : 'var(--ink-soft)',
                                    fontWeight: selectedCategory === category ? 700 : 500,
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    fontFamily: 'inherit',
                                }}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* Results Count */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '32px',
                            flexWrap: 'wrap',
                            gap: '12px',
                        }}
                    >
                        <p
                            style={{
                                fontSize: '14px',
                                color: 'var(--muted)',
                            }}
                        >
                            Showing {filteredPosts.length} articles
                            {selectedCategory !== 'All' && ` in "${selectedCategory}"`}
                        </p>
                        {searchTerm && (
                            <p
                                style={{
                                    fontSize: '14px',
                                    color: 'var(--gold)',
                                    fontWeight: 600,
                                }}
                            >
                                Search results for "{searchTerm}"
                            </p>
                        )}
                    </div>

                    {/* Blog Grid */}
                    {filteredPosts.length > 0 ? (
                        <div className="blog-grid">
                            {filteredPosts.slice(0, visiblePosts).map((post, index) => {
                                const isFeatured = post.is_featured;
                                return (
                                    <Link
                                        to={`/blog/${post.id}`}
                                        key={post.id}
                                        className="blog-card animate-fadeInUp"
                                        style={{
                                            animationDelay: `${(index + 1) * 80}ms`,
                                            position: 'relative',
                                            background: '#fff',
                                            border: isFeatured ? '2px solid var(--gold)' : '1px solid var(--border)',
                                            boxShadow: isFeatured ? 'var(--shadow-gold)' : 'none',
                                        }}
                                    >
                                        {post.is_trending && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '12px',
                                                    right: '12px',
                                                    background: 'var(--gradient-orange)',
                                                    color: '#fff',
                                                    fontSize: '10px',
                                                    fontWeight: 700,
                                                    padding: '4px 12px',
                                                    borderRadius: 'var(--radius-full)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    zIndex: 2,
                                                }}
                                            >
                                                <TrendingUp size={12} />
                                                Trending
                                            </div>
                                        )}

                                        <div
                                            className="blog-cover"
                                            style={{
                                                backgroundImage: `url(${post.featured_image})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                minHeight: '200px',
                                                position: 'relative',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    background:
                                                        'linear-gradient(135deg, rgba(10,22,40,0.2) 0%, rgba(26,46,84,0.1) 100%)',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        bottom: '12px',
                                                        left: '12px',
                                                        background: 'rgba(255,255,255,0.9)',
                                                        padding: '4px 14px',
                                                        borderRadius: 'var(--radius-full)',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        color: 'var(--navy)',
                                                    }}
                                                >
                                                    {post.tag}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="blog-body" style={{ padding: '24px 24px 20px' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '12px',
                                                    marginBottom: '12px',
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        fontSize: '11px',
                                                        color: 'var(--muted)',
                                                    }}
                                                >
                                                    <Calendar size={12} />
                                                    {post.published_at}
                                                </span>
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        fontSize: '11px',
                                                        color: 'var(--muted)',
                                                    }}
                                                >
                                                    <Clock size={12} />
                                                    {post.read_time} min
                                                </span>
                                            </div>

                                            <h3
                                                style={{
                                                    fontSize: '18px',
                                                    color: 'var(--navy)',
                                                    lineHeight: 1.3,
                                                    marginBottom: '8px',
                                                    fontFamily: 'Georgia, serif',
                                                }}
                                            >
                                                {post.title}
                                            </h3>

                                            <p
                                                style={{
                                                    fontSize: '14px',
                                                    color: 'var(--ink-soft)',
                                                    lineHeight: 1.7,
                                                    marginBottom: '16px',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {post.excerpt}
                                            </p>

                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    paddingTop: '14px',
                                                    borderTop: '1px solid var(--border)',
                                                    flexWrap: 'wrap',
                                                    gap: '8px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '50%',
                                                            background: 'var(--cream)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '10px',
                                                            fontWeight: 700,
                                                            color: 'var(--ink-soft)',
                                                        }}
                                                    >
                                                        {post.author?.split(' ').map((name) => name[0]).join('')}
                                                    </div>
                                                    <span
                                                        style={{
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                            color: 'var(--ink-soft)',
                                                        }}
                                                    >
                                                        {post.author?.split(' ')[0]}
                                                    </span>
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        fontSize: '12px',
                                                        color: 'var(--muted)',
                                                    }}
                                                >
                                                    <Heart size={14} />
                                                    {post.likes}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    ) : (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '60px 20px',
                                background: 'var(--cream)',
                                borderRadius: 'var(--radius-lg)',
                            }}
                        >
                            <p
                                style={{
                                    fontSize: '18px',
                                    color: 'var(--ink-soft)',
                                    marginBottom: '8px',
                                }}
                            >
                                No articles found matching your search.
                            </p>
                            <p
                                style={{
                                    fontSize: '14px',
                                    color: 'var(--muted)',
                                }}
                            >
                                Try adjusting your search terms or filter.
                            </p>
                        </div>
                    )}

                    {/* Load More */}
                    {filteredPosts.length > visiblePosts && (
                        <div style={{ textAlign: 'center', marginTop: '48px' }}>
                            <button
                                onClick={() => setVisiblePosts((prev) => prev + 3)}
                                className="btn-ghost"
                                style={{
                                    padding: '14px 32px',
                                    fontSize: '14px',
                                }}
                            >
                                Load more articles
                                <ArrowRight size={18} />
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Newsletter Section - Premium (static) */}
            <section className="premium-cta-section" style={{ padding: '80px 0' }}>
                <div className="wrap">
                    <div className="premium-cta-content" style={{ maxWidth: '600px' }}>
                        <div className="premium-cta-badge">
                            <Mail size={14} style={{ display: 'inline', marginRight: '6px' }} />
                            Stay updated
                        </div>

                        <h2
                            style={{
                                fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
                                marginBottom: '12px',
                            }}
                        >
                            Get weekly insights for <span className="highlight">church leaders</span>
                        </h2>

                        <p
                            style={{
                                fontSize: '16px',
                                color: 'rgba(255,255,255,0.6)',
                                marginBottom: '28px',
                                lineHeight: 1.8,
                            }}
                        >
                            Subscribe to our newsletter for practical tips on member care and church growth.
                        </p>

                        <form
                            style={{
                                display: 'flex',
                                gap: '12px',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                marginBottom: '16px',
                            }}
                        >
                            <input
                                type="email"
                                placeholder="Your email address"
                                style={{
                                    flex: 1,
                                    minWidth: '200px',
                                    padding: '16px 20px',
                                    borderRadius: 'var(--radius-md)',
                                    border: '1.5px solid rgba(255,255,255,0.15)',
                                    background: 'rgba(255,255,255,0.06)',
                                    color: '#fff',
                                    fontSize: '15px',
                                    transition: 'all 0.3s ease',
                                    fontFamily: 'inherit',
                                }}
                                onFocus={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--gold)';
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                                }}
                            />
                            <button type="submit" className="btn-premium-primary large">
                                Subscribe <ArrowRight size={18} className="btn-premium-icon" />
                            </button>
                        </form>

                        <div className="premium-cta-perks">
                            <span>
                                <Bell size={14} />
                                No spam
                            </span>
                            <span>·</span>
                            <span>Unsubscribe anytime</span>
                            <span>·</span>
                            <span>Weekly insights</span>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default Blog;